import { LockClosedIcon, PencilIcon } from '@heroicons/react/24/solid';
import { editorFullName, editorNameWithDate, editorShortName } from '../../utils/editorName';
import type { ConceptualLastEdit } from '../../types/conceptualProblems';
import { MASTHEAD_PHONE_FLEX } from '../table-session/mastheadChrome';

/**
 * Who last edited this problem, in the phone masthead row (feature 029, FR-047).
 *
 * <p><b>Why a pill in the masthead, and not the ⓘ popup.</b> Below `md` the status line is
 * `display:none`, and the masthead row is the only strip that is always on screen. The Super Editor
 * steps through every problem checking whose work it is (client, 2026-09-17); a name that needed a
 * tap per problem would cost them a tap per problem. So the name is always visible here, shortened
 * to "Jane D." to fit beside the icons, and spelled out in full — with the date — in the ⓘ popup for
 * anyone who wants it. Both forms carry the name and date as a hover tooltip; the date never goes on
 * the main screen, which has no room for it.</p>
 *
 * <p><b>Pencil or padlock.</b> The icon says the one thing the Super Editor most needs to see at a
 * glance: whether this problem is still open to editors (pencil) or already settled and locked
 * (padlock, FR-048).</p>
 *
 * <p><b>Height.</b> `text-xs` with `py-0.5` round a 12px icon is shorter than the 28px
 * `MASTHEAD_ICON_BUTTON` beside it, and the masthead takes its height from its tallest control, so
 * the pill costs the strip nothing — the `@layout` test that measures the masthead with and without
 * the categories button measures this too. `max-w-[7rem]` bounds its width, so a long first name
 * truncates instead of pushing the exit tab onto a second line.</p>
 *
 * <p>Phone only (`md:hidden narrow:flex`, the masthead row's own breakpoints): wider screens read the
 * name on the status line. `aria-hidden`, like the row it sits in; the full name is in the popup and
 * in the categories button's accessible name.</p>
 *
 * <p><b>The status-line form</b> (`variant="status"`, client 2026-09-17). On the desktop status line
 * the name read as one more cream label among the facts beside it and was hard to pick out, so it
 * wears the same brass pill there, with the full "Edited by Jane Doe" / "Locked by Jane Doe" wording
 * the line has room for. It is shown at every width the status line is, and it is NOT aria-hidden:
 * on desktop this is where a screen reader meets the name.</p>
 */
export function ConceptualEditorPill({
  lastEdit,
  locked,
  variant = 'masthead',
}: {
  lastEdit: ConceptualLastEdit | null;
  locked: boolean;
  /** `masthead`: phone-only, short name. `status`: the desktop status line, full wording. */
  variant?: 'masthead' | 'status';
}) {
  if (!lastEdit) return null;

  const Icon = locked ? LockClosedIcon : PencilIcon;
  // The date is in the tooltip only — the main screen has no room for it (client, 2026-09-17).
  const tooltip = `${locked ? 'Locked by' : 'Last edited by'} ${editorNameWithDate(lastEdit)}`;

  if (variant === 'status') {
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-brass px-2.5 py-0.5 font-sans text-xs font-semibold text-on-brass"
        data-testid="conceptual-last-editor"
        data-locked={locked ? 'true' : 'false'}
      >
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
        {locked ? 'Locked by ' : 'Edited by '}
        {editorFullName(lastEdit)}
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      title={tooltip}
      className={`flex min-w-0 max-w-[7rem] shrink items-center gap-1 whitespace-nowrap rounded-full bg-brass px-2 py-0.5 font-sans text-xs font-semibold text-on-brass xnarrow:px-1.5 ${MASTHEAD_PHONE_FLEX}`}
      data-testid="conceptual-masthead-editor"
      data-locked={locked ? 'true' : 'false'}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{editorShortName(lastEdit)}</span>
    </span>
  );
}
